import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as commonApi from '../../api/app/commonApi';
import * as api from '../../api/app/monitoredCounterApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { useTranslation } from '../../contexts/translation';
import { AssetDetailTabValues, MonitoredPanelValues } from '../../types/app/asset.types';
import { MonitoredCounter, MonitoredCounterInputs } from '../../types/app/monitoredCounter.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
import { MonitoredCounterForm } from './index';
import { useStyles } from './MonitoredCounterForm.styles';

export function MonitoredCounterDetail() {
  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<MonitoredCounterInputs | null>(null);

  const { mutate: mutateUpdateMonitoredCounter } = useMutation(api.updateMonitoredCounter);
  const { data, isLoading, isError } = useQuery<MonitoredCounter>(['monitoredCounter', id], () =>
    api.getMonitoredCounter(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      parentAssetId: data.parentAssetId,
      opcClientId: data.opcClientId,
      influxValueName: data.influxValueName,
      nodeId: data.nodeId,
      name: data.name,
      main: data.main,
      useMultipleWithHydraPartitioning: data.useMultipleWithHydraPartitioning,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<MonitoredCounterInputs> = (data: MonitoredCounterInputs) => {
    if (!defaultValues) return;

    mutateUpdateMonitoredCounter(
      { ...data },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.monitored-counter.item-edited'), { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.MONITORED_VALUES,
              panelValue: MonitoredPanelValues.MONITORED_COUNTER,
            },
          });
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues || isUsedHydra === undefined) return <Loader />;
  return (
    <>
      <AssetFullPathHeader
        finalTitle={dictionary.MONITORED_COUNTER_EDIT}
        parentId={Number(parentId)}
      />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12} md={8} lg={4}>
              <Paper className={classes.paper}>
                <MonitoredCounterForm
                  onSubmit={handleOnSubmit}
                  isUsedHydra={isUsedHydra}
                  onBack={() =>
                    navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                      state: {
                        tabValue: AssetDetailTabValues.MONITORED_VALUES,
                        panelValue: MonitoredPanelValues.MONITORED_COUNTER,
                      },
                    })
                  }
                  defaultValues={defaultValues}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
